import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { SubAccountListHostActions, SubAccountListHostProps } from 'shared/subAccountList';
import { usePermissions } from '@/providers/Permissions.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useSubAccountPermissions from '@/hooks/useApi/useSubAccountPermissions';
import useSubAccountPermissionLabels from '@/hooks/useSubAccountPermissionLabels';
import adaptAccountPermissions from './Permissions/adaptAccountPermissions';

type HostDrawerActions = Pick<SubAccountListHostActions, 'onOpenUserManagement' | 'onOpenApprovalWorkflow'>;

// The one place client-dashboard maps its own account model, permissions, routes and side effects onto what
// the shared Sub-Account List feature takes as props (ID-13193). Used where the feature is mounted (the
// /sub-accounts-list page). The two drawer actions come from the page because the User Management and
// Approval Workflow drawers are page state; the transfer action is host navigation and lives here.
export default function useSubAccountListHostProps({
  onOpenUserManagement,
  onOpenApprovalWorkflow,
}: HostDrawerActions): SubAccountListHostProps {
  const history = useHistory();
  const { permissions } = usePermissions();
  const canCreateAccountTransfer = Boolean(useModuleFeatureFlag('subAccounts').CreateAccountTransfer);
  const { setNewCurrencyInfo } = useAccountInfo();

  const {
    response: { AccountID, AccountType },
  } = useClientLogin();

  const {
    response: { Country },
  } = useClientData();

  const canAddSubaccounts = !!permissions.AddSubaccounts;
  const canManageBlockedAccounts = !!permissions.ManageBlockedAccounts;

  // The row Permissions drawer's data. Adapted at render, not at fetch, so the labels follow a language switch.
  const {
    fetchingPermissions,
    permissions: rawPermissions,
    fetchSubAccountPermissions,
    saveSubAccountPermissions,
  } = useSubAccountPermissions();

  const labelResolvers = useSubAccountPermissionLabels();

  const permissionSections = useMemo(
    () => adaptAccountPermissions(rawPermissions, labelResolvers),
    [rawPermissions, labelResolvers],
  );

  const hostProps = useMemo<Omit<SubAccountListHostProps, 'permissionsEditor'>>(
    () => ({
      account: {
        accountID: AccountID,
        accountType: AccountType,
        country: Country,
      },
      userPermissions: { canAddSubaccounts, canManageBlockedAccounts, canCreateAccountTransfer },
      hostPaths: {
        subAccountDetail: (accountID: string, currency: string) =>
          `/sub-accounts-list/details/${accountID.replace(/\s+/g, '')}/${currency}`,
      },
      hostActions: {
        onCreateAccountTransfer: ({ accountID, currency, token }) => {
          setNewCurrencyInfo(currency);

          history.push({
            pathname: '/sub-account-transfer',
            state: { token, currency, accountId: accountID },
          });
        },
        onOpenUserManagement,
        onOpenApprovalWorkflow,
      },
    }),
    [
      AccountID,
      AccountType,
      Country,
      canAddSubaccounts,
      canManageBlockedAccounts,
      canCreateAccountTransfer,
      setNewCurrencyInfo,
      history,
      onOpenUserManagement,
      onOpenApprovalWorkflow,
    ],
  );

  // Outside the memo: the hook's fetch / save aren't referentially stable, and the drawer only reads them on open.
  return {
    ...hostProps,
    permissionsEditor: {
      sections: permissionSections,
      fetchingPermissions,
      fetchPermissions: async (accountID) => {
        await fetchSubAccountPermissions({ SubaccountID: accountID });
      },
      savePermissions: async (accountID, values) => {
        await saveSubAccountPermissions({ SubaccountID: accountID, ...values });
      },
    },
  };
}
